import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Spin, Alert, Button, message, Typography, Collapse, Checkbox, Row, Col, Card, Space } from 'antd';
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const RolePermissionManager = () => {
  const [rolePermissions, setRolePermissions] = useState({});
  const [initialRolePermissions, setInitialRolePermissions] = useState({});
  const [allPermissions, setAllPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesRes, allPermsRes] = await Promise.all([
        axios.get('/permissions/roles'),
        axios.get('/permissions/all'),
      ]);
      setRolePermissions(rolesRes.data.data);
      setInitialRolePermissions(JSON.parse(JSON.stringify(rolesRes.data.data))); // Deep copy for reset
      setAllPermissions(allPermsRes.data.data);
    } catch (err) {
      console.error("Error fetching permission data:", err);
      const errorMessage = err.response?.data?.message || 'Failed to fetch permission data.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePermissionChange = (role, permissionKey, isChecked) => {
    setRolePermissions(prev => ({
      ...prev,
      [role]: {
        ...prev[role],
        [permissionKey]: isChecked,
      },
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null); // Clear any previous errors
    
    console.log('Attempting to save role permissions:', rolePermissions);
    
    try {
      const response = await axios.put('/permissions/roles', rolePermissions);
      console.log('Save successful:', response.data);
      
      message.success('Default role permissions updated successfully! The server must be restarted for changes to take full effect.');
      setInitialRolePermissions(JSON.parse(JSON.stringify(rolePermissions))); // Update the reset state
      
      // Refetch data to ensure UI is in sync with server
      await fetchData();
    } catch (err) {
      console.error("Error saving role permissions:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      
      let errorMessage = 'Failed to save changes.';
      
      if (err.response?.status === 403) {
        errorMessage = 'Access denied. You need admin permissions to modify role permissions.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = `Network error: ${err.message}`;
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetChanges = () => {
    console.log('Resetting to initial permissions:', initialRolePermissions);
    setRolePermissions(JSON.parse(JSON.stringify(initialRolePermissions)));
    message.info('Changes have been reset to the last saved state.');
  };

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    return JSON.stringify(rolePermissions) !== JSON.stringify(initialRolePermissions);
  };

  if (loading) {
    return <Spin tip="Loading roles and permissions..." />;
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Manage Default Role Permissions</Title>
          {hasUnsavedChanges() && (
            <Text type="warning" style={{ fontSize: '12px', fontStyle: 'italic' }}>
              You have unsaved changes
            </Text>
          )}
        </div>
        <Space>
          <Button
            icon={<UndoOutlined />}
            onClick={handleResetChanges}
            disabled={isSaving || !hasUnsavedChanges()}
          >
            Reset Changes
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveChanges}
            loading={isSaving}
            disabled={!hasUnsavedChanges()}
          >
            Save Changes
          </Button>
        </Space>
      </div>
      <Alert
        message="Important Note"
        description="Changes saved here will update the default permissions for all users assigned to a role. These changes will only apply after the server is restarted. Individual user permission overrides will not be affected."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      <Collapse accordion>
        {Object.keys(rolePermissions).filter(role => role !== 'Admin').map(role => (
          <Panel header={<Text strong>{role}</Text>} key={role}>
            <Collapse>
              {Object.keys(allPermissions).map(category => (
                <Panel header={category} key={`${role}-${category}`}>
                  <Row gutter={[16, 16]}>
                    {Object.entries(allPermissions[category]).map(([key, perm]) => (
                      <Col xs={24} sm={12} md={8} lg={6} key={key}>
                        <Checkbox
                          checked={!!rolePermissions[role]?.[key]}
                          onChange={(e) => handlePermissionChange(role, key, e.target.checked)}
                        >
                          {perm.name}
                        </Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Panel>
              ))}
            </Collapse>
          </Panel>
        ))}
      </Collapse>
      <div style={{ marginTop: 16 }}>
        <Title level={5}>Admin Role</Title>
        <Text type="secondary">The 'Admin' role always has all permissions by default. This cannot be changed.</Text>
      </div>
    </Card>
  );
};

export default RolePermissionManager;
